import { type FormEvent, type ReactElement, useEffect, useState } from 'react';
import { type FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { getUserConnection } from '@/sgwt/widgets.ts';
import { ApiConfigurationService } from '@/services/ApiConfigurationService.ts'
import { type ApiModel, type ApplicationDto } from '@/models/services.ts'
import { type APIConfigurationDto, type APIConfigurationEntry, type APIConfigurationInformationsDto, type APIEntryDto, type APIEntryExtDto, type CheckExistanceDto, type SaveAPIEntryDto } from '@/models/apiConfiguration.ts'
import { Link } from 'react-router-dom';
import './settingsForm.css';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Form, Label, Input, Nav, NavLink, NavItem,TabContent,TabPane, Row, Col, Card, CardTitle, CardText  } from 'reactstrap';
import type { PingdomCreationDto } from '@/models/pingdom';
import { toastError, toastInfo } from '@/components/common/toast';
import Loading from '../common/Loading';


export type SettingsFormProps = {
    application: ApplicationDto | null,
    closeModal: () => void;
    save: (producerCode: string | undefined, apiEntries : SaveAPIEntryDto[]) => void;
    compute: (producerCode: string | undefined) => void;
    addToPingdom: (webAppKey: string | undefined) => Promise<PingdomCreationDto>;
    
  };

  
const SettingsForm: FC<SettingsFormProps>= ({
    application,
    closeModal,
    save,
    compute,
    addToPingdom
}: SettingsFormProps) => {

  
    const [producerCode, setProducerCode] = useState(application?.producerCode)
    const [activeTab, setActiveTab] = useState(application?.environments[0].name);
    const [activeNavTab, setActiveNavTab] = useState("ApiEnvironments");
    const [apiConfiguration, setApiConfiguration] = useState<APIConfigurationDto | null>(null);
    const [apiConfigurationInformations, setApiConfigurationInformations] = useState<APIConfigurationInformationsDto | null>(null);
    const [apiEntries, setApiEntries] = useState<APIConfigurationEntry[]>([]);
    const [newApiEntries, setNewApiEntries] = useState<APIEntryExtDto[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [alreadyHavePingdom, setAlreadyHavePingdom] = useState<boolean>(false);
    
    const [modalCompute , setModalCompute] = useState(false);
    const toggleCompute = () => setModalCompute(!modalCompute);
    

    useEffect(() => {
        const apiConfigurationService = new ApiConfigurationService();


        if (apiConfiguration == null) {
            setIsLoading(true);

            apiConfigurationService.getConfigureApisInformations(application!.key).then((info: APIConfigurationInformationsDto) => {
                setApiConfigurationInformations(info);
            });

            apiConfigurationService.getConfigureApis(application!.key, application?.environments.map(env => env.name)).then((apisConfig) => {
                    setApiConfiguration(apisConfig);
                    setApiEntries(apisConfig.apiEntries);

                setIsLoading(false);

            })
            .catch((error) => {
                setIsLoading(false);
                toastError(error.message);
            });
        }
    }, [activeTab]);


    const AddApi = () => {
        setNewApiEntries([...newApiEntries, {
            id: newApiEntries.length,
            apiCode: "",
            apiEnvironment: "",
            webappEnvironment: activeTab!,
            isValidApiCode: 0,
            customHealthCheckUri: '',
            possibleEnvironments: null
        }]);        
    };

    const Compute = () => {
      toastInfo("Refresh have been trigger. Result show be visible in few minutes.");
      compute(producerCode);
      toggleCompute();
      setActiveNavTab("ComputeInformations");
      let executeFunction = 0;
      setTimeout(() => {
        if (executeFunction < 6)
        {
            const apiConfigurationService = new ApiConfigurationService();
            apiConfigurationService.getConfigureApisInformations(application!.key).then((info: APIConfigurationInformationsDto) => {
                setApiConfigurationInformations(info);
            });
            executeFunction++;
        }
        }, 10000);  // Waits for 3000 milliseconds (3 seconds)
    };

    const AddToPingdom = () => {
      addToPingdom(application?.key)
      .then((data: PingdomCreationDto) => {
          if (data.status == "Created")
              toastInfo("Pingdom have been added");
          else
              toastError(data.message);
      }).catch((error) => {
          toastError(error.message);
      });
    };

    const SaveSettings = () => {
        const newEntries: SaveAPIEntryDto[] = [];
        apiEntries.map((entry) =>  {
            newEntries.push({
                apiCode: entry.apiCode,
                apiEnvironment: entry.apiEnvironment,
                webappEnvironment: entry.webappEnvironment!,
                customHealthCheckUri: entry.customHealthCheckUri,
            });
        })
        newApiEntries.map((entry) =>  {
            newEntries.push({
                apiCode: entry.apiCode,
                apiEnvironment: entry.apiEnvironment,
                webappEnvironment: entry.webappEnvironment,
                customHealthCheckUri: entry.customHealthCheckUri,
            });
        })
        save(producerCode, newEntries);
    };

    const ChangeApiCode= (event: FormEvent<HTMLInputElement>, api: APIEntryExtDto): void  => {
        api.apiCode = event.currentTarget.value;
        const apiConfigurationService = new ApiConfigurationService();
        apiConfigurationService.checkApiExistance(api.apiCode).then((result: CheckExistanceDto) => {
            const newEntries = [...newApiEntries];
            newEntries[ api.id ].apiCode = api.apiCode;
            newEntries[ api.id ].isValidApiCode = result.apiCodeExist ? 1 : -1;
            newEntries[ api.id ].possibleEnvironments = result.possibleEnvironments;
            newEntries[ api.id ].apiEnvironment = result.possibleEnvironments[0].name;
            setNewApiEntries( newEntries );
        });

    }

    const ChangeEnvironment= (event: FormEvent<HTMLInputElement>, api: APIEntryExtDto): void  => {
        api.apiEnvironment = event.currentTarget.value;
        const newEntries = [...newApiEntries];
        newEntries[ api.id ].apiEnvironment = api.apiEnvironment;
        setNewApiEntries( newEntries );
    }

    const ChangeHealthUri= (event: FormEvent<HTMLInputElement>, api: APIEntryExtDto): void  => {
        api.customHealthCheckUri = event.currentTarget.value;
        const newEntries = [...newApiEntries];
        newEntries[ api.id ].customHealthCheckUri = api.customHealthCheckUri;
        setNewApiEntries( newEntries );
    }
    

    const GetValidity = (validity: number): string => {
        return validity == -1 ? 'is-invalid' : validity== 1 ? 'is-valid' : '';
    }

    const DeleteItem = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, api: any, type: number): void => {
        if (apiEntries != undefined && type != 2) {
            let _apiEntries = [...apiEntries];
            _apiEntries = _apiEntries.filter(obj => !(obj.apiCode == api.apiCode && obj.apiEnvironment == api.apiEnvironment && obj.webappEnvironment == api.webappEnvironment));
            setApiEntries(_apiEntries);
        }
        else if (apiEntries != undefined && type == 2) {
            let _apiEntries = [...newApiEntries];
            _apiEntries = _apiEntries.filter(obj => !(obj.apiCode == api.apiCode && obj.apiEnvironment == api.apiEnvironment && obj.webappEnvironment == api.webappEnvironment));
            setNewApiEntries(_apiEntries);
        }
    }

    const SetApiEnvenvironment = (api: APIConfigurationEntry, value: string) => {
        api.apiEnvironment = value;
        const newEntries = [...apiEntries];
        newEntries.filter( (f) => f.apiCode == api.apiCode ).map((api) => 
            {
                if (api.webappEnvironment == activeTab)
                    api.apiEnvironment = api.apiEnvironment;
            });
        setApiEntries( newEntries );
    };

    const SetApiHealthUri = (api: APIConfigurationEntry, value: string) => {
        api.customHealthCheckUri = value;
        const newEntries = [...apiEntries];
        newEntries.filter( (f) => f.apiCode == api.apiCode ).map((api) => 
            {
                if (api.webappEnvironment == activeTab)
                    api.customHealthCheckUri = api.customHealthCheckUri;
            });
        setApiEntries( newEntries );
    };

    

  return (
    <div className="row pt-2  d-flex">
        <ModalHeader>Settings for {application?.name}</ModalHeader>
        <ModalBody>
        <Nav tabs>
            <NavItem>
                <NavLink className={activeNavTab == "ApiEnvironments" ? 'active' : ''} onClick={() => setActiveNavTab("ApiEnvironments")}>Api Environments</NavLink>
            </NavItem>
            <NavItem>
                <NavLink className={activeNavTab == "PartialIncident" ? 'active' : ''} onClick={() => setActiveNavTab("PartialIncident")}>Partial Incident</NavLink>
            </NavItem>
            <NavItem>
                <NavLink className={activeNavTab == "ComputeInformations" ? 'active' : ''} onClick={() => setActiveNavTab("ComputeInformations")}>Compute Informations</NavLink>
            </NavItem>
        </Nav>
        <TabPane tabId="ApiEnvironments" className={activeNavTab == "ApiEnvironments" ? 'tabPane-env active' : 'tabPane-env'}>

            <Form className='pt-3'>          
                <Label className='form-card-env-title'>Api Environments:</Label>  
                <FormGroup className='form-group-card'>
                <p className='mb-2 fst-italic'>Please provide, for each environments, the list of Apis that your service is using.</p>  
                <Nav tabs>
                    {application?.environments.map((env => (
                    <NavItem>
                    <NavLink className={activeTab == env.name ? 'active' : ''} onClick={() => setActiveTab(env.name)}>
                        {env.name}
                    </NavLink>
                    </NavItem>
                    )))}
                </Nav>
                {isLoading ? <Loading isLoading={isLoading} /> : <TabContent activeTab={activeTab}>
                
                    {application?.environments?.map((env => (
                    <TabPane tabId={env.name} className='tabPane-env'>
                    <Row className='tab-max-height'>
                        <Col sm="12">
                        <h6 className='mb-2'>Apis</h6>
                        { apiEntries.filter(apiEnv => apiEnv.webappEnvironment == env.name)?.map((api => (
                            <Row className='mb-2'>
                                <Col sm="3" className='text-v-align'>{api.apiCode}:</Col>
                                <Col sm="4">
                                        <Input type="select" onChange={event => SetApiEnvenvironment(api, event.target.value)} >
                                        {
                                            apiEntries?.filter( (f) => f.apiCode == api.apiCode )[0]?.possibleApiEnvironments?.map((environment) =>  {
                                                if (environment.name == api.apiEnvironment)
                                                    return <option selected>{ environment.name }</option>
                                                else if (environment.name == activeTab)
                                                    return <option selected>{ environment.name }</option>
                                                return <option>{ environment.name }</option>
                                            })
                                        }
                                        </Input>
                                </Col>
                                <Col sm="4">
                                    <Input
                                        type="text"
                                        onChange={event => SetApiHealthUri(api, event.target.value)}
                                        value={
                                            api.customHealthCheckUri === null
                                                ? (
                                                    apiEntries
                                                        ?.find((f) => f.apiCode === api.apiCode)
                                                        ?.possibleApiEnvironments &&
                                                    (
                                                        api.apiEnvironment === null || api.apiEnvironment === ''
                                                            ? (
                                                                apiEntries
                                                                    .find((f) => f.apiCode === api.apiCode)
                                                                    ?.possibleApiEnvironments
                                                                    ?.find((env) => env.name === activeTab)
                                                                    ?.healthCheckUri ?? apiEntries
                                                                    .find((f) => f.apiCode === api.apiCode)
                                                                    ?.possibleApiEnvironments?.[0]?.healthCheckUri ?? ""
                                                            )
                                                            : apiEntries
                                                                .find((f) => f.apiCode === api.apiCode)
                                                                ?.possibleApiEnvironments
                                                                ?.find((env) => env.name === api.apiEnvironment)
                                                                ?.healthCheckUri ?? apiEntries
                                                                .find((f) => f.apiCode === api.apiCode)
                                                                ?.possibleApiEnvironments[0]
                                                                ?.healthCheckUri ?? ""
                                                    )
                                                )
                                                : api.customHealthCheckUri
                                        }
                                    />
                                </Col>
                                <Col>
                                    <button type="button" className="btn btn-icon btn-outline-primary" title="delete item" onClick={event => DeleteItem(event, api, 1)}>
                                        <em className="icon">delete</em>
                                    </button>
                                </Col>
                            </Row>

                        )))}
                        { newApiEntries.filter((f) => f.webappEnvironment == activeTab).map((api => (
                            <Row className='mb-2'>
                                <Col sm="3" className='text-v-align'>
                                <Input type="text" placeholder="Enter Api Code" onInput={(evt) => ChangeApiCode(evt, api)}  className={GetValidity(api.isValidApiCode)} defaultValue={api.apiCode}></Input>
                                </Col>
                                <Col sm="4" className='text-v-align'>
                                    <Input type="select" onChange={event => ChangeEnvironment(event, api)}>
                                        {
                                            api?.possibleEnvironments?.map((environment) =>  {
                                                if (api.apiEnvironment == environment.name)
                                                    return <option selected>{ environment.name }</option>
                                                return <option>{ environment.name }</option>
                                            })
                                        }
                                        </Input>
                                </Col>
                                <Col sm="4" className='text-v-align'>
                                    <Input type="text" onChange={event => ChangeHealthUri(event, api)} value={
                                            api.customHealthCheckUri === ''
                                                ? (
                                                    newApiEntries
                                                        ?.find((f) => f.apiCode === api.apiCode)
                                                        ?.possibleEnvironments &&
                                                    (
                                                        api.apiEnvironment === null || api.apiEnvironment === ''
                                                            ? (
                                                                newApiEntries
                                                                    .find((f) => f.apiCode === api.apiCode)
                                                                    ?.possibleEnvironments
                                                                    ?.find((env) => env.name === activeTab)
                                                                    ?.healthCheckUri ?? newApiEntries
                                                                    .find((f) => f.apiCode === api.apiCode)
                                                                    ?.possibleEnvironments?.[0]?.healthCheckUri ?? "--"
                                                            )
                                                            : newApiEntries
                                                                .find((f) => f.apiCode === api.apiCode)
                                                                ?.possibleEnvironments
                                                                ?.find((env) => env.name === api.apiEnvironment)
                                                                ?.healthCheckUri ?? "++"
                                                    )
                                                )
                                                : api.customHealthCheckUri
                                        }></Input>
                                </Col>
                                <Col>
                                    <button type="button" className="btn btn-icon btn-outline-primary" title="delete item" onClick={event => DeleteItem(event, api, 2)}>
                                        <em className="icon">delete</em>
                                    </button>
                                </Col>
                            </Row>
                        )))
                        }
                        </Col>
                    </Row>
                    <Row>
                        <Col sm="12" className='text-end'>
                            <Button color="primary" onClick={AddApi}><i className="icon icon-md me-2">add</i>Add</Button>
                        </Col>
                    </Row>
                    </TabPane>
                    )))}
                </TabContent>
                }
                </FormGroup>
            </Form>
        </TabPane>
        <TabPane tabId="PartialIncident" className={activeNavTab == "PartialIncident" ? 'tabPane-env active' : 'tabPane-env'}>
            <Form className='pt-3'>
                <Label className='form-card-title'>Partial Incident:</Label>
                <FormGroup className='form-group-card'>
                <p className='mb-2 fst-italic'>Please provide, is you're using one, the producer code that your service is using for partial incicent.</p>  
                <Label for="producerCode">Producer code:</Label>
                    <Input
                        id="producerCode"
                        name="producerCode"
                        placeholder="Enter your producer code"
                        type="text"
                        onChange={event => setProducerCode(event.target.value)}
                        value={producerCode}
                    />
                </FormGroup>
            </Form>
        </TabPane>
        <TabPane tabId="ComputeInformations" className={activeNavTab == "ComputeInformations" ? 'tabPane-env active' : 'tabPane-env'}>
            <Form className='pt-3'>   
                <Label className='form-card-env-title'>Update Informations:</Label>  
                <FormGroup className='form-group-card'>
                <p className='mb-2 fst-italic'>Last information regarding the batch processing of this webApp</p>  
                <table>
                    <tr>
                        <td>Last Update:</td><td>{apiConfigurationInformations?.updateDate != null && apiConfigurationInformations?.updateDate !== '' ? apiConfigurationInformations?.updateDate : "N/A"}</td>
                    </tr>
                    {apiConfigurationInformations?.batchInfo != null ?
                    <>
                    <tr><td></td>&nbsp;<td>&nbsp;</td></tr>
                    <tr><td>Last Batch Start:</td><td>{apiConfigurationInformations?.batchInfo?.startDateFormatted ?? "N/A"}</td></tr>
                    <tr><td>Last Batch End:</td><td>{apiConfigurationInformations?.batchInfo?.endDateFormatted ?? "N/A"}</td></tr>
                    <tr><td>Last Batch Status:</td><td>{apiConfigurationInformations?.batchInfo?.statusFormated ?? "N/A"}</td></tr>
                    <tr><td>Last Batch Message:</td><td>{apiConfigurationInformations?.batchInfo?.message ?? ""}</td></tr>
                    </>: null }
                </table>
                </FormGroup>
            </Form>

        </TabPane>



        
        </ModalBody>
        <ModalFooter>
            {!alreadyHavePingdom ?
            <Button color="primary" onClick={AddToPingdom}>
            <i className="icon icon-md me-2">monitor_heart</i>Add to pingdom
            </Button>
            : null 
            }
            <Button color="primary" onClick={Compute}>
            <i className="icon icon-md me-2">refresh</i>Compute
            </Button>
            <Button color="primary" onClick={SaveSettings}>
            <i className="icon icon-md me-2">save</i>Save
            </Button>
            <Button color="secondary" onClick={closeModal}>
            <i className="icon icon-md me-2">cancel</i>Cancel
            </Button>
        </ModalFooter>
        {/* <Modal isOpen={modalCompute}  toggle={toggleCompute}>
            <div  className='shadow-max border border-danger'>
            <ModalHeader  toggle={toggleCompute}>Compute Triggered</ModalHeader>
            <ModalBody>
                A Compute Job has been triggered for the service {application?.name} in background<br />
                It will take a few minutes to complete, you can close this window and come back later to see the results.
            </ModalBody>
            </div>
        </Modal> */}

    </div>
  );
};

export default SettingsForm;